import { gql, TypedDocumentNode } from '@apollo/client';

export enum ReturnMethodValue {
  OriginalPaymentMethod,
  SiteCredit
}

export type ReturnMethod = {
  __typename: 'RestReturnMethod';
  name: string;
  value: ReturnMethodValue | number;
};

export type ReturnSummary = {
  __typename: 'RestReturnSummary';
  id: string;
  type: 'return_summary' | 'request';
  attributes: {
    __typename: 'RestReturnSummmaryAttributes';
    countryCode: string;
    currencyUnit: string;
    orderNumber: string;
    refundDetails: {
      __typename: 'RestRefundDetails';
      amount: string;
      paymentMethod: string;
    }[];
    returnDetails: {
      __typename: 'RestRefundDetails';
      amount: string;
      paymentMethod: string;
    }[];
    returnFeeDetails: {
      __typename: 'RestReturnFeeDetails';
      message: string;
      returnShippingFee: string;
    };
    returnMethods: ReturnMethod[];
    returnMethodId: number | null;
    returnOptionId: number;
    returnReasonHash: { [id: string]: string };
    returnSiteCreditBonus: boolean;
    returnSiteCreditBonusAmount: string | number | null;
    returnSiteCreditBonusPercentage: number;
    selectedItemIds: string[];
    shipmentNumber: string | null;
    taxes: string | number;
    itemRefundAmount: string | number;
    taxDetails: {
      __typename: 'RestReturnSummaryTaxDetails';
      taxType: string;
      refundable: boolean;
    };
    totalRefundAmount: string | number;
  };
};

export type GetReturnSummaryQueryVariables = {
  shipmentNumber?: string;
  orderNumber?: string;
  countryCode: string;
};

export type GetReturnSummaryQuery = {
  getReturnSummary: {
    __typename: 'RestReturnSummaryPayload';
    data: ReturnSummary[];
  };
};

export const RETURN_SUMMARY_FRAGMENT = gql`
  fragment ReturnSummaryFragment on RestReturnSummary {
    id
    type
    attributes @type(name: "RestReturnSummmaryAttributes") {
      countryCode: country_code
      currencyUnit: currency_unit
      orderNumber: order_number
      refundDetails: refund_summary_details @type(name: "RestRefundDetails") {
        amount
        paymentMethod: payment_method
      }
      returnDetails: return_summary @type(name: "RestReturnDetails") {
        amount
        paymentMethod: payment_method
      }
      returnMethods: return_method_details @type(name: "RestReturnMethod") {
        name
        value
      }
      returnFeeDetails: return_fee_detail @type(name: "RestReturnFeeDetails") {
        message
        returnShippingFee: return_shipping_fee
      }
      returnMethodId: return_method_id
      returnOptionId: return_option_id
      returnReasonHash: return_reason_hash
      returnSiteCreditBonus: return_site_credit_bonus
      returnSiteCreditBonusAmount: return_site_credit_bonus_amount
      returnSiteCreditBonusPercentage: return_site_credit_bonus_percentage
      selectedItemIds: selected_item_ids
      shipmentNumber: shipment_number
      taxes
      itemRefundAmount: items_refund_amount
      taxDetails: tax_details @type(name: "RestReturnSummaryTaxDetails") {
        taxType: tax_type
        refundable
      }
      totalRefundAmount: total_refund_amount
    }
  }
`;

export const GET_RETURN_SUMMARY_QUERY: TypedDocumentNode<
  GetReturnSummaryQuery,
  GetReturnSummaryQueryVariables
> = gql`
  query GetReturnSummaryQuery(
    $shipmentNumber: String
    $orderNumber: String
    $countryCode: String!
  ) {
    getReturnSummary(
      shipment_number: $shipmentNumber
      order_number: $orderNumber
      country_code: $countryCode
    )
      @rest(
        type: "RestReturnSummaryPayload"
        endpoint: "return_summary"
        path: "/v3.5/return_summary?{args}"
      ) {
      data @type(name: "RestReturnSummary") {
        ...ReturnSummaryFragment
      }
    }
  }
  ${RETURN_SUMMARY_FRAGMENT}
`;
